/* Lightweight PWA helpers — service worker registration, install prompt,
 * notification permission, and a simple booking-alert poller.
 *
 * We do NOT ship a full VAPID push stack; instead the admin PWA polls the
 * backend every 45s while open, and any brand-new confirmed booking triggers a
 * native OS notification via the service worker. This is enough for a one-owner
 * business and works on iOS Safari 16.4+ once the app is added to home screen.
 */

const SEEN_KEY = "wl_admin_seen_booking_ids";
const LAST_POLL_KEY = "wl_admin_last_poll_ts";

export const isPwa = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

export const registerServiceWorker = () => {
  if (!("serviceWorker" in navigator)) return;
  // register after the app is idle so we don't block first paint
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch(() => {});
  });
};

/* Install prompt — the browser dispatches beforeinstallprompt on Android/desktop.
 * iOS never fires it; we detect iOS separately and show manual instructions. */
let deferredInstallPrompt = null;
const installPromptListeners = new Set();

export const initInstallPrompt = () => {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    installPromptListeners.forEach((cb) => cb(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installPromptListeners.forEach((cb) => cb(false));
  });
};

export const canInstall = () => !!deferredInstallPrompt;
export const onInstallAvailable = (cb) => {
  installPromptListeners.add(cb);
  cb(canInstall());
  return () => installPromptListeners.delete(cb);
};
export const triggerInstall = async () => {
  if (!deferredInstallPrompt) return { outcome: "unavailable" };
  deferredInstallPrompt.prompt();
  const result = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  installPromptListeners.forEach((cb) => cb(false));
  return result;
};

export const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

/* Notifications */
export const requestNotifPermission = async () => {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try { return await Notification.requestPermission(); } catch { return "denied"; }
};

/* ---------- Real Web-Push (VAPID) subscribe/unsubscribe ---------- */
const urlBase64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
};

export const getPushSubscription = async () => {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
};

export const subscribeToPush = async ({ apiBase, briefTime = "07:00" }) => {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("Push not supported.");
  const perm = await requestNotifPermission();
  if (perm !== "granted") throw new Error("Notifications blocked.");
  const reg = await navigator.serviceWorker.ready;
  // fetch the VAPID key
  const keyRes = await fetch(`${apiBase}/api/admin/push/vapid-key`);
  const { public_key } = await keyRes.json();
  if (!public_key) throw new Error("Server has no VAPID key.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }
  const token = localStorage.getItem("wl_admin_token");
  const body = {
    subscription: sub.toJSON(),
    brief_time: briefTime,
    tz_offset_min: -new Date().getTimezoneOffset(),
    label: navigator.userAgent.split(")")[0].split("(")[1] || "This device",
  };
  const res = await fetch(`${apiBase}/api/admin/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Server rejected subscription (${res.status})`);
  return sub;
};

export const unsubscribeFromPush = async ({ apiBase }) => {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  const token = localStorage.getItem("wl_admin_token");
  await fetch(`${apiBase}/api/admin/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const sendTestPush = async ({ apiBase }) => {
  const token = localStorage.getItem("wl_admin_token");
  const res = await fetch(`${apiBase}/api/admin/push/test`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Test failed (${res.status})`);
  return res.json();
};

const notify = async (title, body, url) => {
  if (Notification.permission !== "granted") return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg && reg.active) {
    reg.active.postMessage({ type: "notify", title, body, tag: "wl-booking", url });
  } else if ("Notification" in window) {
    // eslint-disable-next-line no-new
    new Notification(title, { body });
  }
};

/* Polling for new bookings ------------------------------------------------- */
let pollTimer = null;

const seenSet = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
};
const saveSeen = (set) => {
  const arr = [...set].slice(-500);
  localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
};

export const startBookingPoll = (getAuthToken, apiBase, intervalMs = 45000) => {
  stopBookingPoll();
  const tick = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const res = await fetch(`${apiBase}/api/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const items = Array.isArray(data) ? data : (data.bookings || []);
      const seen = seenSet();
      const firstRun = seen.size === 0;
      const fresh = [];
      for (const b of items) {
        const id = b.id || b._id;
        if (!id) continue;
        if (!seen.has(id)) {
          seen.add(id);
          if (!firstRun && (b.payment_status === "paid" || b.payment_status === "pending")) fresh.push(b);
        }
      }
      saveSeen(seen);
      localStorage.setItem(LAST_POLL_KEY, String(Date.now()));
      // If this device has a real push subscription, the server already pings us
      // instantly on every new booking — skip poll-based alerts to avoid doubles.
      let hasPush = false;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        hasPush = !!(reg && (await reg.pushManager.getSubscription()));
      } catch { /* ignore */ }
      if (hasPush) return;
      // On first run we just seed the seen-set, we don't notify for historical bookings.
      for (const b of fresh.slice(0, 3)) {
        await notify(
          `New booking · ${b.guest_name || "Guest"}`,
          `${b.sub_type || b.unit_id || "Stay"} · ${b.start_date} → ${b.end_date} · £${b.total}`,
          "/admin?tab=bookings",
        );
      }
    } catch { /* offline — try again next tick */ }
  };
  tick();
  pollTimer = setInterval(tick, intervalMs);
};

export const stopBookingPoll = () => {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
};
